import { useState, useEffect, useRef, useMemo } from "react";
import { AppBar, Toolbar, Avatar, Box, IconButton, Drawer, Typography, Button, Snackbar, useMediaQuery, useTheme, Divider, Select, MenuItem, CircularProgress, Backdrop, Badge, Dialog, DialogTitle, DialogContent, TextField, List, ListItemButton, ListItemText, ListItemIcon, InputAdornment, ListItem, Grow } from "@mui/material";
import { InputBase, Paper } from "@mui/material";
import { useNavigate, useLocation, Link, NavLink } from "react-router-dom";
import { Menu as MenuIcon, Notifications as NotificationsIcon, Search as SearchIcon, History as HistoryIcon, Clear as ClearIcon, LockOutlined as LockOutlinedIcon, BusinessOutlined as BusinessOutlinedIcon } from "@mui/icons-material";
import { CiPower } from "react-icons/ci";
import { FaUserCircle } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import Menu from "./Menu";
import { useMenuData } from "./menuData";
import { useAuth } from "../context/AuthContext";
import { useRights } from "../context/RightsContext";
import axios from "axios";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchDialog from "../components/SearchDialog";
import { Tabs, Tab, Chip } from "@mui/material";
import { Popover } from "@mui/material";
import Notification from "./Notification";
import { useNavNotifications } from "../context/NotificationContext";
import CloseIcon from "@mui/icons-material/Close";
import { DTZ, formatWithTZ } from "../utils/TimeZone";
import { APPBAR_HEIGHT } from "../utils/theme";

const Navigation = ({ connectionString, drawerOpen, setDrawerOpen, appVersion }) => {
  const { user, companyData = [], selectedCompany, switchCompany, logout, triggerLogout } = useAuth();
  const { canAccess, showAccessDenied, rightsLoading, rightsReady } = useRights() || {};
  const { menuData, loading: menuLoading, error: menuError } = useMenuData(connectionString, selectedCompany?.AppRegID, selectedCompany?.CompId);
  const { notifications, unreadCount, handleNotificationClick, handleMarkAllRead, newNotifications, clearNewNotifications, browserNotifEnabled, requestNotificationPermission } = useNavNotifications();
  const isRegularUser = user?.UserCategory === "User";
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const [mobileOpen, setMobileOpen] = useState(false);
  // const [drawerOpen, setDrawerOpen] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [tempCompanyId, setTempCompanyId] = useState(selectedCompany?.CompId || "");
  const [switchingCompany, setSwitchingCompany] = useState(false);
  // Notifications
  // const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  // Search
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [recentlyVisited, setRecentlyVisited] = useState([]);
  // Inside Navigation component, add this state
  const [sidebarSearchQuery, setSidebarSearchQuery] = useState("");
  const userMenuRef = useRef(null);
  const notificationRef = useRef(null);
  const API = `${connectionString}/api`;
  const [scrolled, setScrolled] = useState(false);
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [menuDialogOpen, setMenuDialogOpen] = useState(false);
  const [dialogMenuKey, setDialogMenuKey] = useState(null);

  // Add this ref to track toggle clicks
  const toggleClicked = useRef(false);
  const notificationDrawerRef = useRef(null);
  // theme-based z-index base to avoid magic numbers
  const modalZ = (theme?.zIndex?.modal ?? 1200);

  //notification toast state and variables
  const [toastOpen, setToastOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [flyoutOpen, setFlyoutOpen] = useState(null);
  const [flyoutPos, setFlyoutPos] = useState({ top: 100, left: 70 });
  const flyoutAnchorRef = useRef(null);
  const flyoutRef = useRef(null);
  const preventSearchReopen = useRef(false);

  const handleExpand = () => setExpanded(true);
  const handleCollapse = () => setExpanded(false);
  const handleToggle = () => setExpanded(prev => !prev);
  const companyBtnRef = useRef(null); // <-- ADD THIS

  useEffect(() => {
    if (newNotifications.length > 0) {
      setToastOpen(true);
    }
  }, [newNotifications.length]);

  useEffect(() => {
    const handleScroll = () => { setScrolled(window.scrollY > 20); };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-close toast after 4 seconds if not expanded
  useEffect(() => {
    if (toastOpen && !expanded) {
      const timer = setTimeout(() => {
        setToastOpen(false);
        clearNewNotifications();
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [toastOpen, expanded, clearNewNotifications]);

  // Load recently visited
  useEffect(() => {
    try {
      const saved = localStorage.getItem("recentMenuItems");
      if (saved) setRecentlyVisited(JSON.parse(saved).slice(0, 6));
    } catch (e) { /* ignore */ }
  }, []);

  // Save recent visit
  const saveRecent = (item) => {
    try {
      const recent = JSON.parse(localStorage.getItem("recentMenuItems") || "[]");
      const filtered = recent.filter(i => i.link !== item.link);
      filtered.unshift(item);
      localStorage.setItem("recentMenuItems", JSON.stringify(filtered.slice(0, 10)));
      setRecentlyVisited(filtered.slice(0, 6));
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (toggleClicked.current) {
        toggleClicked.current = false;
        return;
      }

      if (flyoutOpen && flyoutAnchorRef.current && !flyoutAnchorRef.current.contains(e.target) && flyoutRef.current && !flyoutRef.current.contains(e.target)) {
        setFlyoutOpen(null);
      }

      if (searchOpen && e.target.closest(".MuiDialog-root")) return;

      const inUserMenu = userMenuRef.current?.contains(e.target);
      // For notification drawer, rely on onClose handler instead of manual click detection
      // since it's rendered in a portal
      const inNotifications = notificationRef.current?.contains(e.target);

      if (!inUserMenu) {
        setShowUserMenu(false);
        setCompanyDropdownOpen(false);
      }
      if (!inNotifications && !showNotifications) {
        setShowNotifications(false);
      }
    };

    // document.addEventListener("click", handleClickOutside, false);
    // return () => document.removeEventListener("click", handleClickOutside, false);
    // run on mousedown so our onMouseDown toggle guard works reliably (portals/popovers)
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [searchOpen, flyoutOpen, flyoutAnchorRef, flyoutRef, showNotifications]);

  const getMessageLabel = (msg = "") => {
    if (msg === "Inquiry Follow-up") return "FL";
    if (msg === "New Inquiry") return "IN";
    if (msg === "New Quotation") return "QT";
    if (msg === "New Sale - Order") return "SO";
    if (msg === "Sale - Challan") return "SC";
    if (msg === "Sale - Invoic") return "SI";
    if (msg === "New Purchase Order") return "PO";
    if (msg === "Purchase - Challan") return "PC";
    if (msg === "Purchase - Invoice") return "PI";
    return msg;
  };

  // Stable hash function for avatar colors
  const getStableColorIndex = (id) => {
    if (!id) return 0;
    let hash = 0;
    const str = String(id);
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash);
  };

  // Close search dialog automatically when route changes
  useEffect(() => {
    // close when pathname changes
    setSearchOpen(false);
    setSearchQuery("");
    setSidebarSearchQuery("");
    preventSearchReopen.current = false;
    setFlyoutOpen(null);
    // no cleanup needed
  }, [location.pathname]);

  useEffect(() => { setTempCompanyId(selectedCompany?.CompId || ""); }, [selectedCompany]);

  const currentCompany = selectedCompany || companyData[0];
  const companyName = currentCompany?.CompanyName || "No Company Selected";

  const showUnauthorized = () => setSnackbarOpen(true);
  const closeSnackbar = () => setSnackbarOpen(false);

  const handleChangeCompany = async () => {
    const newComp = companyData.find(c => c.CompId === tempCompanyId);
    if (!newComp || newComp.CompId === selectedCompany?.CompId) return;
    setSwitchingCompany(true);
    try {
      await switchCompany(newComp);
      setShowUserMenu(false);
    } catch (err) {
      console.error("Switch company failed:", err);
      setSnackbarOpen(true);
    } finally {
      setSwitchingCompany(false);
    }
  };

  const handleLogout = () => {
    setShowUserMenu(false);
    triggerLogout();
  };

  // Update toggle handlers to set the flag
  const toggleUserMenu = () => {
    toggleClicked.current = true;
    setShowUserMenu(prev => !prev);
  };

  const handleNotificationToggle = () => {
    toggleClicked.current = true;
    setShowNotifications(prev => !prev);
  };

  // Search items
  const allMenuItems = useMemo(() => {
    const items = [];
    Object.values(menuData).forEach(cat => {
      if (cat.link) items.push({ label: cat.label, link: cat.link });
      if (cat.subFields) {
        Object.values(cat.subFields).forEach(sub => items.push({ label: sub.label, link: sub.link }));
      }
    });
    return items;
  }, [menuData]);

  const filteredItems = allMenuItems.filter(item => item.label.toLowerCase().includes(searchQuery.toLowerCase()));

  const handleCloseSearch = () => {
    setSearchOpen(false);
    setSearchQuery("");
    preventSearchReopen.current = false;
  };

  const handleSearchSelect = (item) => {
    try {
      setSearchQuery("");
      setSearchOpen(false);
      saveRecent(item);
      navigate(item.link);
      if (isMobile) setMobileOpen(false);
    } catch (err) {
      console.error("handleSearchSelect error:", err);
    }
  };

  const [notifTab, setNotifTab] = useState(0); // 0 = Unread, 1 = Read

  const unreadNotifications = useMemo(() => notifications.filter(n => !n.read), [notifications]);
  const readNotifications = useMemo(() => notifications.filter(n => n.read), [notifications]);

  // Replace your drawerContent with this:
  const drawerContent = (
    <Box sx={{ width: isDesktop ? (drawerOpen ? 250 : 66) : 250, transition: "width 0.3s ease", display: "flex", flexDirection: "column", height: "100vh", }} >
      {/* Fixed Header: Logo + Search */}
      <Box sx={{ flexShrink: 0 }}>
        {/* Logo Section */}
        {/* <Box sx={{ textAlign: "center", py: drawerOpen ? 0.55 : 1.85, px: 1 }}>
          <Link to="/main/home">
            <Box component="img" src={drawerOpen ? "/reontest3.svg" : "/ReonBookFav.jpeg"} alt="Logo" sx={{ height: drawerOpen ? 48 : 27, width: drawerOpen ? 110 : 34, objectFit: "contain", transition: "all 0.3s ease", }} />
          </Link>
        </Box> */}
        <Box sx={{
          textAlign: "center",
          py: drawerOpen ? 1 : 1.85,
          px: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }}>
          <Link to="/main/home" style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Box
              component="img"
              src={drawerOpen ? "/web01.svg" : "/ReonBookFav.png"}
              alt="Logo"
              sx={{
                height: drawerOpen ? 48 : 33,    // ← increased
                width: drawerOpen ? 160 : 34,    // ← increased
                objectFit: "contain",
                display: "block",
                transition: "all 0.3s ease",
              }}
            />
          </Link>
        </Box>
        <Divider sx={{ mb: 0.5 }} />

        {/* Search Box (when expanded) */}
        {drawerOpen && isDesktop && (
          <Box sx={{ p: 2, py: 1 }}>
            <Paper elevation={3} sx={{ p: "2px 8px", display: "flex", alignItems: "center", borderRadius: 2 }}>
              <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
              <InputBase placeholder="Search menu..." value={sidebarSearchQuery} onChange={(e) => setSidebarSearchQuery(e.target.value)} sx={{ flex: 1, fontSize: "0.9rem" }} onKeyDown={(e) => { if (e.key === "Enter") { setSearchOpen(true); setSearchQuery(sidebarSearchQuery); } else { e.stopPropagation(); } }} inputProps={{ "aria-label": "Search menu" }}
                onClick={() => {
                  setSearchOpen(true);
                  setSearchQuery(sidebarSearchQuery);
                }}
              />
              {sidebarSearchQuery ? (
                <IconButton onClick={() => setSidebarSearchQuery("")} sx={{ width: 40, height: 40 }}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : (
                <IconButton sx={{ width: 40, height: 40 }}
                  onClick={() => {
                    setSearchOpen(true);
                    setSearchQuery(sidebarSearchQuery);
                  }}
                >
                  <SearchIcon fontSize="small" />
                </IconButton>
              )}
            </Paper>
          </Box>
        )}

        {/* Collapsed Search Button */}
        {!drawerOpen && isDesktop && (
          <Box sx={{ display: "flex", justifyContent: "center", pb: 2 }}>
            <IconButton onClick={() => setSearchOpen(true)} size="large" sx={{ width: 34, height: 34, bgcolor: "#fff", color: "primary.main", boxShadow: "0 4px 12px rgba(24,119,242,0.20)", "&:hover": { transform: "scale(1.1)", bgcolor: "primary.light" }, }} >
              <SearchIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Scrollable Menu Area */}
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", px: 1, }} >
        {menuLoading ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, px: 1, py: 2 }}>
            {[...Array(6)].map((_, i) => (
              <Box key={i} sx={{ height: 40, borderRadius: 1, bgcolor: "grey.100", animation: "pulse 1.5s ease-in-out infinite" }} />
            ))}
          </Box>
        ) : menuError ? (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="error">
              Unable to load menu. Please refresh the page.
            </Typography>
          </Box>
        ) : (
        <Menu menuDataOverride={menuData} selectedCompany={selectedCompany} drawerOpen={drawerOpen} isMobile={isMobile} user={user} showUnauthorized={showUnauthorized} saveRecent={saveRecent} closeMobileMenu={() => setMobileOpen(false)} canAccess={canAccess} showAccessDenied={showAccessDenied} rightsLoading={rightsLoading} rightsReady={rightsReady} appVersion={appVersion}
          onFlyoutRequest={(key, element) => {
            flyoutAnchorRef.current = element;
            if (element) {
              const rect = element.getBoundingClientRect();
              setFlyoutPos({ top: rect.bottom - 60, left: rect.right + 12 });
            }
            setFlyoutOpen(key);
          }}
        />
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {/* Global Search Dialog */}
      <SearchDialog open={searchOpen} initialQuery={searchQuery} menuItems={allMenuItems} recentlyVisited={recentlyVisited} isMobile={isMobile}
        onClose={() => {
          preventSearchReopen.current = true;
          setSearchOpen(false);
          setSidebarSearchQuery("");
          setSearchQuery("");
        }}
        onNavigate={(link) => {
          setSearchOpen(false);          // ✅ force close
          setSidebarSearchQuery("");     // ✅ clear sidebar input
          setSearchQuery("");
          navigate(link);
          if (isMobile) setMobileOpen(false);
        }}

      />

      {/* Backdrop */}
      <Backdrop open={switchingCompany} sx={{ color: "#fff", zIndex: modalZ + 4, flexDirection: "column", backdropFilter: "blur(4px)" }}>
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6" sx={{ mt: 3 }}> Switching to {companyData.find(c => c.CompId === tempCompanyId)?.CompanyName || "Company"}... </Typography>
      </Backdrop>

      <Box sx={{ display: "flex" }}>

        {/* Sidebar - already permanent on desktop */}
        <Drawer variant={!isDesktop ? "temporary" : "permanent"} open={!isDesktop ? mobileOpen : true} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }} sx={{ width: isDesktop ? (drawerOpen ? 250 : 66) : 250, flexShrink: 0, "& .MuiDrawer-paper": { width: isDesktop ? (drawerOpen ? 250 : 66) : 250, top: 0, height: "100vh", overflowX: "hidden", boxShadow: "2px 0 12px rgba(0,0,0,0.08)", borderRight: "1px solid rgba(0,0,0,0.07)", transition: "width 0.3s ease", bgcolor: "#FFFFFF", }, }} >
          {drawerContent}
        </Drawer>

        {/* Flyout Sidebar - only when drawer collapsed */}
        {!drawerOpen && !isMobile && flyoutOpen && (
          <Box ref={flyoutRef} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} sx={{ position: "fixed", top: flyoutPos.top, left: flyoutPos.left, width: 280, maxHeight: "70vh", overflowY: "auto", background: "white", borderRadius: 3, boxShadow: "0 20px 60px rgba(0,0,0,0.18)", border: "1px solid rgba(0,0,0,0.08)", zIndex: modalZ + 3, py: 1, }} >
            {/* ←←← ARROW GOES HERE ←←← */}
            <Box sx={{ position: "absolute", left: 6, top: 28, width: 0, height: 0, borderTop: "10px solid transparent", borderBottom: "10px solid transparent", borderRight: "10px solid #000", zIndex: 100000, }} />
            <Box sx={{ position: "absolute", left: 6, top: 28, width: 0, height: 0, borderTop: "11px solid transparent", borderBottom: "11px solid transparent", borderRight: "11px solid rgba(0,0,0,0.6)", zIndex: 99999, }} />

            {/* Header */}
            <Box sx={{ p: 2, pl: 4, borderBottom: "1px solid", borderColor: "divider", bgcolor: "grey.50" }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {menuData[flyoutOpen]?.label}
              </Typography>
            </Box>

            {/* Submenu Items */}
            <List sx={{ py: 0 }}>
              {Object.entries(menuData[flyoutOpen]?.subFields || {})
                .filter(([subKey]) => !(flyoutOpen === "Marketing" && subKey === "reassign2" && isRegularUser))
                .map(([subKey, sub]) => (
                  <ListItem key={subKey} disablePadding>
                    <ListItemButton component={NavLink} to={sub.link} sx={{ py: 1.2, px: 2.5 }}
                      onClick={() => {
                        saveRecent({ label: sub.label, link: sub.link });
                        setFlyoutOpen(null);
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, color: "text.secondary" }}>
                        {sub.icon}
                      </ListItemIcon>
                      <ListItemText primary={sub.label} primaryTypographyProps={{ fontSize: "0.875rem" }} />
                    </ListItemButton>
                  </ListItem>
                ))}
            </List>
          </Box>
        )}

        {/* AppBar - now will be offset by parent layout */}
        <AppBar position="fixed" elevation={0} sx={{ width: { lg: `calc(100% - ${drawerOpen ? 250 : 66}px)` }, ml: { lg: `${drawerOpen ? 250 : 66}px` }, bgcolor: "#FFFFFF", borderBottom: scrolled ? "1px solid rgba(0,0,0,0.12)" : "1px solid rgba(0,0,0,0.07)", transition: "box-shadow 0.25s ease, border-color 0.25s ease", boxShadow: scrolled ? "0 2px 12px rgba(0,0,0,0.08)" : "none", }} >
          <Toolbar sx={{ minHeight: isMobile ? 56 : APPBAR_HEIGHT, color: "black", px: { xs: 1, sm: 2 } }}>
            <IconButton onClick={isMobile ? () => setMobileOpen(!mobileOpen) : () => setDrawerOpen(!drawerOpen)} sx={{ mr: 1, width: 40, height: 40, borderRadius: 1.5, "&:hover": { bgcolor: "rgba(0,0,0,0.06)" }, }} >
              <MenuIcon sx={{ fontSize: 24 }} />
            </IconButton>

            <Box sx={{ flexGrow: 1 }} />

            {/* Company Name (Desktop) */}
            {!isMobile && (<Typography variant="body2" fontWeight={600} sx={{ mr: 2, color: "text.secondary", fontSize: "0.85rem", letterSpacing: "0.01em" }} noWrap>{companyName}</Typography>)}

            {/* Right action group */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>

            {/* Notification Bell */}
            <Box ref={notificationRef} sx={{ position: "relative" }}>
              <IconButton
                aria-label="Notifications"
                onMouseDown={() => { toggleClicked.current = true; }}
                onClick={() => { setShowNotifications(prev => !prev); }}
                sx={{
                  width: 40, height: 40,
                  borderRadius: 2,
                  bgcolor: showNotifications ? "rgba(24,119,242,0.1)" : "transparent",
                  color: "text.primary",
                  "&:hover": { bgcolor: "rgba(24,119,242,0.08)" },
                  ...(unreadCount > 0 && {
                    animation: "realisticBellRing 1.8s ease-out infinite",
                    "& .MuiBadge-badge": { animation: "none !important" },
                  }),
                  "@keyframes realisticBellRing": {
                    "0%": { transform: "rotate(0deg)" },
                    "8%": { transform: "rotate(16deg)" },
                    "16%": { transform: "rotate(-14deg)" },
                    "24%": { transform: "rotate(12deg)" },
                    "32%": { transform: "rotate(-10deg)" },
                    "40%": { transform: "rotate(8deg)" },
                    "48%": { transform: "rotate(-6deg)" },
                    "56%": { transform: "rotate(4deg)" },
                    "64%": { transform: "rotate(-2deg)" },
                    "72%": { transform: "rotate(1deg)" },
                    "80%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(0deg)" },
                  },
                }}
              >
                {unreadCount > 0 ? (
                  <Badge badgeContent={unreadCount} color="error" overlap="circular" anchorOrigin={{ vertical: "top", horizontal: "right" }} sx={{ "& .MuiBadge-badge": { fontSize: "0.65rem", minWidth: 16, height: 16 } }}>
                    <NotificationsIcon sx={{ fontSize: 22, color: unreadCount ? "#1877F2" : "inherit" }} />
                  </Badge>
                ) : (
                  <NotificationsIcon sx={{ fontSize: 22 }} />
                )}
              </IconButton>

              {/* Notification Drawer */}
              <Drawer anchor="right" open={showNotifications} onClose={() => setShowNotifications(false)} ModalProps={{ sx: { zIndex: modalZ + 1 } }} PaperProps={{ sx: { width: { xs: "100%", sm: 390, md: 400 }, boxShadow: "0 24px 64px rgba(0,0,0,0.16)", borderTopLeftRadius: 16, borderBottomLeftRadius: 16 } }}>
                <Box ref={notificationDrawerRef} sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#fff", overflow: "hidden" }}>

                  {/* Header */}
                  <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: "#e7f0fd", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <NotificationsIcon sx={{ fontSize: 20, color: "#1877F2" }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={700} fontSize="0.95rem" color="#0f172a">Notifications</Typography>
                      <Typography fontSize="0.75rem" color="text.secondary">
                        {unreadNotifications.length > 0 ? `${unreadNotifications.length} unread` : "All caught up"}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setShowNotifications(false)} sx={{ color: "text.secondary", bgcolor: "grey.100", borderRadius: 1.5, "&:hover": { bgcolor: "grey.200" } }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Tabs */}
                  <Tabs value={notifTab} onChange={(e, v) => setNotifTab(v)} variant="fullWidth"
                    sx={{
                      borderBottom: 1, borderColor: "divider", minHeight: 44,
                      "& .MuiTab-root": { minHeight: 44, fontSize: "0.82rem", fontWeight: 600, textTransform: "none" },
                      "& .Mui-selected": { color: "#1877F2" },
                      "& .MuiTabs-indicator": { bgcolor: "#1877F2", height: 3, borderRadius: 2 },
                    }}
                  >
                    <Tab label={
                      <Box display="flex" alignItems="center" gap={0.8}>
                        Unread
                        {unreadNotifications.length > 0 && (
                          <Chip size="small" label={unreadNotifications.length} sx={{ height: 18, fontSize: "0.68rem", fontWeight: 700, bgcolor: "#1877F2", color: "#fff" }} />
                        )}
                      </Box>
                    } />
                    <Tab label="Read" />
                  </Tabs>

                  {/* List */}
                  <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                    {notifLoading ? (
                      <Box sx={{ p: 5, textAlign: "center" }}> <CircularProgress size={32} /> </Box>
                    ) : (notifTab === 0 ? unreadNotifications : readNotifications).length === 0 ? (
                      <Box sx={{ p: 6, textAlign: "center", color: "text.secondary" }}>
                        <Typography> {notifTab === 0 ? "No unread notifications 🎉" : "No read notifications"} </Typography>
                      </Box>
                    ) : (
                      (notifTab === 0 ? unreadNotifications : readNotifications).map(
                        (notif, index) => {
                          const name = (notif.client || "User").trim();
                          const colors = ["#e91e63", "#9c27b0", "#673ab7", "#3f51b5", "#4caf50", "#8bc34a", "#ffc107", "#ff9800", "#ff5722", "#795548", "#607d8b", "#2196f3", "#03a9f4", "#00bcd4", "#009688"];
                          const colorIndex = getStableColorIndex(notif.id);
                          const bgColor = colors[colorIndex % colors.length];

                          return (
                            <Box
                              key={notif.id}
                              onClick={() => handleNotificationClick(notif.id)}
                              sx={{
                                px: 2.5, py: 2,
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                cursor: "pointer",
                                bgcolor: notif.read ? "background.paper" : "primary.50",
                                borderLeft: "3px solid",
                                borderLeftColor: notif.read ? "transparent" : "primary.main",
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1.5,
                                "&:hover": { bgcolor: "action.hover" },
                              }}
                            >
                              {/* Avatar */}
                              <Avatar sx={{ width: 40, height: 40, bgcolor: bgColor, color: "#fff", fontWeight: 700, flexShrink: 0 }}>
                                {name[0].toUpperCase()}
                              </Avatar>

                              {/* Content */}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                {/* First row: name + type chip */}
                                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 0.3 }}>
                                  <Typography fontWeight={notif.read ? 400 : 600} sx={{ wordBreak: "break-word", lineHeight: 1.4 }}>
                                    {notif.client}
                                  </Typography>
                                  {notif.message && (
                                    <Chip label={getMessageLabel(notif.message)} size="small" color="secondary" sx={{ flexShrink: 0, height: 20, fontSize: "0.72rem", fontWeight: 600 }} />
                                  )}
                                </Box>

                                <Typography fontSize="0.82rem" color="text.secondary" fontWeight={500}>
                                  Doc: {notif.documentNumber}
                                </Typography>

                                {(notif.remarks && notif.remarks !== "N/A") && (
                                  <Typography fontSize="0.8rem" color="text.secondary" sx={{ fontStyle: "italic", mt: 0.3, wordBreak: "break-word" }}>
                                    {notif.remarks}
                                  </Typography>
                                )}

                                {(notif.leadFrom || notif.leadTo) && (
                                  <Box sx={{ mt: 0.3 }}>
                                    {notif.leadFrom && <Typography fontSize="0.78rem" color="text.secondary"><strong>From:</strong> {notif.leadFrom}</Typography>}
                                    {notif.leadTo && <Typography fontSize="0.78rem" color="text.secondary"><strong>To:</strong> {notif.leadTo}</Typography>}
                                  </Box>
                                )}

                                <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: "block" }}>
                                  {notif.raw?.ist_datetime ? (() => { try { return formatWithTZ(notif.raw.ist_datetime); } catch { return notif.time; } })() : notif.time}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        }
                      )
                    )}
                  </Box>

                  {/* Footer */}
                  {notifTab === 0 && unreadNotifications.length > 0 && (
                    <Box sx={{ px: 2.5, py: 1.8, borderTop: "1px solid", borderColor: "divider", bgcolor: "#fafafa" }}>
                      <Button fullWidth size="small" variant="outlined" onClick={handleMarkAllRead}
                        sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: "0.82rem", borderColor: "#1877F2", color: "#1877F2", "&:hover": { bgcolor: "#e7f0fd", borderColor: "#1877F2" } }}>
                        Mark all as read
                      </Button>
                    </Box>
                  )}
                </Box>
              </Drawer>
            </Box>

            {/* User Avatar Button */}
            <Box ref={userMenuRef} sx={{ position: "relative", mr: { xs: 0.5, sm: 1 } }}>
              <IconButton
                aria-label="User menu"
                aria-expanded={showUserMenu}
                onClick={toggleUserMenu}
                onMouseDown={() => { toggleClicked.current = true; }}
                sx={{ p: 0.5, borderRadius: 2 }}
              >
                <Avatar sx={{
                  width: 36, height: 36,
                  bgcolor: "#1877F2",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  border: "2px solid",
                  borderColor: showUserMenu ? "#1464cc" : "rgba(24,119,242,0.25)",
                  boxShadow: showUserMenu ? "0 0 0 3px rgba(24,119,242,0.18)" : "none",
                  transition: "all 0.2s ease",
                }}>
                  {(user?.EmployeeName || user?.UserName || "?")[0].toUpperCase()}
                </Avatar>
              </IconButton>

              {/* User Menu Popup */}
              {user && (
                <Box
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  sx={{
                    position: "absolute", top: 50, right: 0,
                    width: 300, maxWidth: "92vw",
                    bgcolor: "#fff",
                    borderRadius: 3,
                    overflow: "hidden",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08)",
                    border: "1px solid rgba(0,0,0,0.07)",
                    zIndex: modalZ + 2,
                    opacity: showUserMenu ? 1 : 0,
                    transform: showUserMenu ? "translateY(0) scale(1)" : "translateY(-8px) scale(0.97)",
                    visibility: showUserMenu ? "visible" : "hidden",
                    pointerEvents: showUserMenu ? "auto" : "none",
                    transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
                  }}
                >
                  {/* Profile Header */}
                  <Box sx={{
                    background: "linear-gradient(135deg, #1877F2 0%, #1464cc 80%)",
                    px: 3, pt: 3, pb: 2.5,
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 1.2,
                  }}>
                    <Avatar sx={{
                      width: 60, height: 60,
                      bgcolor: "rgba(255,255,255,0.18)",
                      border: "3px solid rgba(255,255,255,0.45)",
                      fontSize: "1.7rem", fontWeight: 700, color: "#fff",
                    }}>
                      {(user.EmployeeName || user.UserName || "?")[0].toUpperCase()}
                    </Avatar>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography fontWeight={700} fontSize="0.95rem" color="#fff" lineHeight={1.3}>
                        {user.EmployeeName || user.UserName}
                      </Typography>
                      <Typography fontSize="0.75rem" sx={{ color: "rgba(255,255,255,0.72)", mt: 0.3 }}>
                        {user.UserCategory}{user.UserName ? ` · ${user.UserName}` : ""}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Company Switcher */}
                  <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.2 }}>
                      <BusinessOutlinedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                      <Typography variant="caption" fontWeight={700} color="text.disabled" sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        Active Company
                      </Typography>
                    </Box>

                    <Box sx={{ position: "relative" }}>
                      <Button
                        ref={companyBtnRef}
                        fullWidth variant="outlined" size="medium"
                        sx={{ justifyContent: "space-between", textTransform: "none", fontWeight: 500, py: 1.1, borderRadius: 2, mb: 1.2, fontSize: "0.82rem", borderColor: "rgba(0,0,0,0.18)", color: "text.primary", "&:hover": { borderColor: "#1877F2" } }}
                        endIcon={<Box sx={{ transform: companyDropdownOpen ? "rotate(180deg)" : "none", transition: "0.2s", display: "flex" }}><ArrowDropDownIcon /></Box>}
                        onClick={(e) => { e.stopPropagation(); setCompanyDropdownOpen(prev => !prev); }}
                      >
                        <Typography noWrap fontSize="0.82rem">
                          {companyData.find(c => c.CompId === tempCompanyId)?.CompanyName || "Select Company"}
                        </Typography>
                      </Button>

                      {/* Company Dropdown — logic unchanged */}
                      {companyDropdownOpen && (
                        <Popover open={companyDropdownOpen} anchorEl={companyBtnRef.current} onClose={() => setCompanyDropdownOpen(false)} disableAutoFocus disableEnforceFocus anchorOrigin={{ vertical: "bottom", horizontal: "left" }} transformOrigin={{ vertical: "top", horizontal: "left" }}
                          PaperProps={{ sx: { width: companyBtnRef.current ? companyBtnRef.current.offsetWidth : 280, maxHeight: "40vh", overflowY: "auto", overscrollBehavior: "contain", WebkitOverflowScrolling: "touch", scrollbarGutter: "stable", borderRadius: 2, boxShadow: "0 8px 24px rgba(0,0,0,0.14)", border: "1px solid rgba(0,0,0,0.1)" } }}
                        >
                          <Box onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                            {companyData?.map((comp) => (
                              <MenuItem key={comp.CompId} selected={comp.CompId === tempCompanyId}
                                sx={{ py: 1.4, px: 2.5, justifyContent: "space-between", backgroundColor: comp.CompId === tempCompanyId ? "#e7f0fd" : "transparent", "&:hover": { backgroundColor: "action.hover" } }}
                                onClick={(e) => { e.stopPropagation(); setTempCompanyId(comp.CompId); setCompanyDropdownOpen(false); }}
                              >
                                <Typography variant="body2" fontWeight={comp.CompId === tempCompanyId ? 600 : 400}>
                                  {comp.CompanyName}
                                </Typography>
                                {selectedCompany?.CompId === comp.CompId && (
                                  <Chip label="Active" size="small" sx={{ height: 18, fontSize: "0.68rem", fontWeight: 700, bgcolor: "#1877F2", color: "#fff" }} />
                                )}
                              </MenuItem>
                            ))}
                          </Box>
                        </Popover>
                      )}
                    </Box>

                    <Button fullWidth variant="contained" size="medium" onClick={handleChangeCompany}
                      disabled={!tempCompanyId || switchingCompany || tempCompanyId === selectedCompany?.CompId}
                      sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, py: 1.1, fontSize: "0.82rem", bgcolor: "#1877F2", "&:hover": { bgcolor: "#1464cc" }, "&:disabled": { bgcolor: "action.disabledBackground" } }}
                    >
                      {switchingCompany ? "Switching..." : "Switch Company"}
                    </Button>
                  </Box>

                  <Divider />

                  {/* Action Items */}
                  <Box sx={{ p: 1 }}>
                    <MenuItem
                      onClick={() => { navigate("/main/change-password"); setShowUserMenu(false); }}
                      sx={{ borderRadius: 1.5, py: 1.2, px: 2, gap: 1.5, "&:hover": { bgcolor: "#f8fafc" } }}
                    >
                      <LockOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                      <Typography fontSize="0.875rem" fontWeight={500} color="text.primary">Change Password</Typography>
                    </MenuItem>
                    <MenuItem
                      onClick={handleLogout}
                      sx={{ borderRadius: 1.5, py: 1.2, px: 2, gap: 1.5, "&:hover": { bgcolor: "#fff5f5" } }}
                    >
                      <CiPower size={18} color="#ef4444" />
                      <Typography fontSize="0.875rem" fontWeight={600} color="error.main">Sign Out</Typography>
                    </MenuItem>
                  </Box>
                </Box>
              )}
            </Box>

            </Box>{/* end right action group */}
          </Toolbar>
        </AppBar>
      </Box>

      {/* ✅ Custom Notification */}
      <Notification open={toastOpen} notifications={newNotifications} expanded={expanded} onExpand={handleExpand} onCollapse={handleCollapse} onToggle={handleToggle}
        onClose={() => {
          setToastOpen(false);
          setExpanded(false);
          clearNewNotifications()
        }}
      />
      <Toolbar />
      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={closeSnackbar} message="403: Unauthorized access" anchorOrigin={{ vertical: "bottom", horizontal: "left" }} />
    </>
  );
};

export default Navigation;